import { useCallback } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { RadioGroupV2 } from '@/components/@IntranetLibrary/Radio/RadioV2';
import {
  useHtmlToReact,
  type ElementTransform,
} from '@/components/Common/HtmlToReact';
import {
  ApprovalFormTable,
  ApprovalValueBox,
} from '@/components/Approval/@components/ApprovalFormTable';
import { InfoSectionTitle } from '@/components/Approval/@components/InfoSection';
import type { VehicleCostEmployee } from '@/utils/approval/vehicleCost';
import {
  calculateServicePeriod,
  RESIGNATION_OTHER_DETAIL,
  RESIGNATION_PERSONAL_REASON,
  RESIGNATION_REASON_DETAILS,
  RESIGNATION_REASONS,
  type ResignationData,
  type ResignationFormState,
} from '@/utils/approval/resignation';
import { PledgeBox, ResignationViewRoot } from './styles';

export { PledgeBox, ResignationRoot } from './styles';

/** 레거시 서약서 문구 — 원문 그대로(번역 없음) */
export const RESIGNATION_PLEDGE_INTRO =
  '상기 본인은 귀사를 퇴직한 후 다음에 규정한 사항들을 준수하고, 만약 이를 어길 시에는 귀사의 본인에 대한 어떤 불이익에도 감수할 것이며 또한, 귀사가 제시하는 조건이나 행위, 민, 형사상 법적조치에도 이의를 제기하지 않을 것을 서약합니다.';

export const RESIGNATION_PLEDGE_ITEMS = [
  '본인은 귀사에 재직 중 지득한 기술, 노하우, 제품의 생산방법, 기술관리방법, 연구개발 및 교육훈련 자료, 사업계획, 개발계획, AI 활용하는 과정 중 취득한 일체의 정보 및 자산 (AI 에이전트, 프롬프트, 알고리즘, 학습 데이터, AI 활용 업무 프로세스, AI 관련 교육·연구 자료, 대시보드 설계 및 운영 노하우 등을 포함하며 이에 한정하지 아니함), 협력업체 및 관계회사의 사업정보와 기타의 제반 영업비밀에 관한 사항을 제3자에게 공개, 누설, 전송하거나 교육·자문·기술지원 등의 형태로 제공하지 않겠습니다.',
  '퇴사월 급여는 정기 급여 지급일에 지급하는데 동의한다.',
  '퇴직금은 퇴사월 급여 지급일 이후 14일 이내에 지급하는데 동의한다.',
  '본인이 지득하고 있는 귀사의 영업비밀을 이용하여 부정한 이익을 취득하거나 또는 귀사에게 손해를 입힐 목적으로 이를 사용하거나, 타인에게 공개하지 아니한다.',
  '본인이 재직기간 알게된 인사정보를 이용하여 창업이나 경쟁회사에 입사시킬 목적으로 귀사에 재직중인 인력의 회유 등 어떠한 행위도 하지 않는다.',
  '향후 귀사 및 귀사의 브랜드, 귀사의 임직원에 대한 이미지 저해행위 및 평판에 누가 되는 행위, 명예를 훼손 시킬 수 있는 법안이나 행위 등 어떠한 명예훼손 및 위해언행을 하지 않는다.',
  '퇴사 전 SA 상의 모든 결재상신 및 승인 건에 대한 업무처리를 완료한다.',
];

export const RESIGNATION_PLEDGE_ALERT =
  '서약서에 동의해야 품의 상신이 가능합니다.';

/** 서약서 본문 — 작성 화면과 저장 HTML 공용 */
export const ResignationPledgeText = () => (
  <>
    {RESIGNATION_PLEDGE_INTRO}
    <div className="text-center mt-15">- 다 음 -</div>
    <ol className="mt-15">
      {RESIGNATION_PLEDGE_ITEMS.map((item) => (
        <li key={item}>{item}</li>
      ))}
    </ol>
  </>
);

export const ResignationEmployeeTable = ({
  employee,
}: {
  employee: Pick<
    VehicleCostEmployee,
    'name' | 'employeeNumber' | 'affiliation' | 'enterDate'
  >;
}) => {
  const { t } = useTranslation();
  return (
    <ApprovalFormTable>
      <tbody>
        <tr>
          <th>{t('이름')}</th>
          <th>{t('사번')}</th>
          <th>{t('소속')}</th>
          <th>{t('입사일')}</th>
        </tr>
        <tr>
          <td className="text-center" style={{ whiteSpace: 'nowrap' }}>
            {employee.name}
          </td>
          <td className="text-center">{employee.employeeNumber}</td>
          <td className="text-center">{employee.affiliation}</td>
          <td className="text-center">{employee.enterDate}</td>
        </tr>
      </tbody>
    </ApprovalFormTable>
  );
};

const hidden = { display: 'none' } as const;
const shownIf = (show: boolean) => ({ display: show ? 'block' : 'none' });

type MarkupProps = { state: ResignationFormState; t: TFunction };

/** 레거시 #emp_info_content 안쪽 마크업(id·class 동일) — 저장 시점 선택값을 담아 둠 */
const ResignationContentMarkup = ({ state, t }: MarkupProps) => {
  const isPersonal = state.reason === RESIGNATION_PERSONAL_REASON;
  return (
    <>
      <div className="pb-20">
        <strong className="d-block mb-10">
          {t('퇴직 사유')}
          <span className="hide-for-view" style={hidden}>
            {' '}
            {t('작성')}
          </span>
        </strong>
        <div className="pb-20">
          <span className="hide-for-view" style={hidden}>
            <label className="label view-bold">{t('퇴직 사유')}</label>
          </span>
          <label className="select" style={{ maxWidth: 150 }}>
            <select id="reason" disabled defaultValue={state.reason}>
              <option value="" disabled>
                {t('퇴직 사유 선택')}
              </option>
              {RESIGNATION_REASONS.map((reason) => (
                <option key={reason} value={reason}>
                  {t(reason)}
                </option>
              ))}
            </select>
            <i />
          </label>
        </div>
        <div id="reasonDetail" style={shownIf(isPersonal)}>
          <label className="label">{t('상세사유')}</label>
          <ul className="reason-details">
            {RESIGNATION_REASON_DETAILS.map((detail) => (
              <li key={detail}>
                <label>
                  <input
                    type="radio"
                    name="reasonDetail"
                    value={detail}
                    disabled
                    defaultChecked={state.reasonDetail === detail}
                  />{' '}
                  {t(detail)}
                </label>
              </li>
            ))}
          </ul>
          <div
            id="reasonDetailWrite"
            className="textarea mt-5"
            style={shownIf(state.reasonDetail === RESIGNATION_OTHER_DETAIL)}
          >
            <textarea
              placeholder={t('사유 작성')}
              className="comment-content"
              disabled
              defaultValue={state.reasonDetailWrite}
            />
          </div>
        </div>
      </div>
      <div className="pb-20 mb-10">
        <strong className="d-block mb-10">
          {t('퇴직 서약서')}
          <span className="hide-for-view" style={hidden}>
            {' '}
            {t('동의')}
          </span>
        </strong>
        <div className="retirement-pledge">
          <ResignationPledgeText />
        </div>
        <div id="pledgeAgree" className="mt-15">
          <div className="d-flex justify-content-end">
            <label className="mr-15">
              <input
                type="radio"
                name="pledgeAgree"
                value="Y"
                disabled
                defaultChecked={state.pledgeAgree === 'Y'}
              />{' '}
              {t('동의함')}
            </label>
            <label>
              <input
                type="radio"
                name="pledgeAgree"
                value="N"
                disabled
                defaultChecked={state.pledgeAgree === 'N'}
              />{' '}
              {t('동의하지 않음')}
            </label>
          </div>
          <p
            id="pledgeAlert"
            className="text-right alert-text"
            style={shownIf(state.pledgeAgree === 'N')}
          >
            {t(RESIGNATION_PLEDGE_ALERT)}
          </p>
        </div>
      </div>
    </>
  );
};

/** attributes.content — 레거시 조회 화면은 위 표 2개 아래에 이 HTML 을 그대로 출력 */
export const buildResignationContentHtml = (
  state: ResignationFormState,
  t: TFunction,
): string =>
  renderToStaticMarkup(<ResignationContentMarkup state={state} t={t} />);

const DROP_TAGS = new Set(['style', 'script', 'link', 'i']);

const radioValueByName = (
  data: ResignationData | null,
  name: string | null,
): string | undefined => {
  if (!data) return undefined;
  if (name === 'reasonDetail') return data.reason_detail ?? '';
  if (name === 'pledgeAgree') return data.pledge_agree;
  return undefined;
};

/** 레거시 조회처럼 select 표시값 — 저장된 option 문구(작성자 언어) 우선 */
const selectedOptionText = (
  element: Element,
  value: string | undefined,
): string => {
  const options = Array.from(element.querySelectorAll('option'));
  const match =
    value !== undefined
      ? options.find((o) => o.getAttribute('value') === value)
      : options.find((o) => o.hasAttribute('selected'));
  return match?.textContent?.trim() || value || '';
};

type RadioGroupSource = {
  name: string | null;
  row: boolean;
  checked: string;
  options: { value: string; label: string }[];
};

/** 레거시 라디오 묶음(상세사유 ul, 서약 동의 줄) → 공통 라디오 그룹 입력값 */
const radioGroupOf = (element: Element): RadioGroupSource | null => {
  const isDetailList = element.classList.contains('reason-details');
  const isPledgeRow =
    element.classList.contains('d-flex') &&
    element.parentElement?.id === 'pledgeAgree';
  if (!isDetailList && !isPledgeRow) return null;
  const inputs = Array.from(
    element.querySelectorAll<HTMLInputElement>('input[type=radio]'),
  );
  if (!inputs.length) return null;
  return {
    name: inputs[0].getAttribute('name'),
    row: isPledgeRow,
    checked:
      inputs.find((i) => i.hasAttribute('checked'))?.getAttribute('value') ??
      '',
    options: inputs.map((i) => ({
      value: i.getAttribute('value') ?? '',
      label: i.closest('label')?.textContent?.trim() ?? '',
    })),
  };
};

// 조회 전용이라 비활성이어도 본문색, 크기는 표·값 박스 글자와 같은 14px
const viewRadioLabelSx = {
  '& .MuiFormControlLabel-root.Mui-disabled .MuiFormControlLabel-label.Mui-disabled':
    {
      color: 'var(--dk-text, #1A1A1A)',
      fontSize: 14,
      lineHeight: '20px',
    },
};
const viewRadioColumnSx = { gap: '8px', ...viewRadioLabelSx };
const viewRadioRowSx = { justifyContent: 'flex-end', ...viewRadioLabelSx };
const reasonWriteSx = {
  alignItems: 'flex-start',
  minHeight: 84,
  padding: '10px 12px',
  whiteSpace: 'pre-wrap',
};

type SnapshotProps = { content: string; data: ResignationData | null };

/** 저장 content(레거시 문서 포함) 그대로 — 선택값은 레거시 조회처럼 data 로 채움 */
const ResignationSnapshot = ({ content, data }: SnapshotProps) => {
  const transform = useCallback<ElementTransform>(
    ({ element, tagName, props, children }) => {
      if (DROP_TAGS.has(tagName)) return null;
      if (element.classList.contains('hide-for-view')) return null;
      const key = props.key as string;
      if (tagName === 'strong') {
        return (
          <InfoSectionTitle key={key} sx={{ mb: 1 }}>
            {children}
          </InfoSectionTitle>
        );
      }
      if (tagName === 'select') {
        return (
          <ApprovalValueBox key={key} filled sx={{ maxWidth: 150 }}>
            {selectedOptionText(element, data?.reason)}
          </ApprovalValueBox>
        );
      }
      if (tagName === 'textarea') {
        return (
          <ApprovalValueBox key={key} filled sx={reasonWriteSx}>
            {data ? data.reason_detail_write : element.textContent}
          </ApprovalValueBox>
        );
      }
      const radios = radioGroupOf(element);
      if (radios) {
        const picked = radioValueByName(data, radios.name);
        return (
          <RadioGroupV2
            key={key}
            row={radios.row}
            disabled
            value={picked ?? radios.checked}
            options={radios.options}
            sx={radios.row ? viewRadioRowSx : viewRadioColumnSx}
          />
        );
      }
      if (element.classList.contains('retirement-pledge')) {
        return <PledgeBox key={key}>{children}</PledgeBox>;
      }
      return undefined;
    },
    [data],
  );
  return <>{useHtmlToReact(content, { transform })}</>;
};

type ViewProps = { content: string | null; data: ResignationData | null };

/** 조회 — 레거시 appr_view_forms_resignation: 퇴사 정보·날짜 표는 data, 아래는 저장 content */
export const ResignationView = ({ content, data }: ViewProps) => {
  const { t } = useTranslation();
  const period = data
    ? calculateServicePeriod(data.emp_gr_end_date, data.retire_date)
    : null;
  return (
    <ResignationViewRoot>
      {data && (
        <div className="pb-20 mb-10">
          <InfoSectionTitle sx={{ mb: 1 }}>{t('퇴사 정보')}</InfoSectionTitle>
          <ResignationEmployeeTable
            employee={{
              name: data.emp_name,
              employeeNumber: data.emp_no,
              affiliation: data.emp_company,
              enterDate: data.emp_gr_end_date,
            }}
          />
          <div style={{ marginTop: 20 }} />
          <ApprovalFormTable>
            <tbody>
              <tr>
                <th>{t('마지막 근무일')}</th>
                <th>{t('퇴직일')}</th>
                <th>{t('근속기간')}</th>
              </tr>
              <tr>
                <td className="text-center">{data.last_work_date}</td>
                <td className="text-center">{data.retire_date}</td>
                <td className="text-center">
                  {period
                    ? t('{{years}}년 {{months}}개월', {
                        years: period.years,
                        months: period.months,
                      })
                    : ''}
                </td>
              </tr>
            </tbody>
          </ApprovalFormTable>
        </div>
      )}
      {content && <ResignationSnapshot content={content} data={data} />}
    </ResignationViewRoot>
  );
};
